import { ChevronDown } from '@/components/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  IMPLICIT_PRIMITIVE_LABELS,
  ImplicitPrimitive,
  ImplicitPrimitiveParams,
  GYROID_SPHERE_DEFAULTS,
  SUPER_ELLIPSOID_DEFAULTS,
  LATTICE_PIPE_DEFAULTS,
  GYROID_GENUS_DEFAULTS,
  BASE_BOX_DEFAULTS,
  BASE_SPHERE_DEFAULTS,
  BASE_CYLINDER_DEFAULTS,
  BASE_PIPE_DEFAULTS,
  BASE_PIPE_SEGMENT_DEFAULTS,
  BASE_RING_DEFAULTS,
  BASE_LENS_DEFAULTS,
  LATTICE_MANIFOLD_DEFAULTS,
} from '@/config/implicitPrimitives';

import {
  pickerContentClass,
  pickerItemInactiveClass,
  pillWidthClass,
} from '../dropdownStyles';

import { PRIMITIVE_ORDER, SubTab } from './data';

interface ImplicitPrimitivesDropdownProps {
  tab: SubTab;
  vehicleSubTab: string;
  implicitPrimitive: ImplicitPrimitive;
  setVehicleSubTab: (subTab: string) => void;
  setImplicitPrimitive: (p: ImplicitPrimitive) => void;
  setImplicitPrimitiveParams: (p: ImplicitPrimitiveParams) => void;
}

export function ImplicitPrimitivesDropdown({
  tab,
  vehicleSubTab,
  implicitPrimitive,
  setVehicleSubTab,
  setImplicitPrimitive,
  setImplicitPrimitiveParams,
}: ImplicitPrimitivesDropdownProps) {
  const isActive = vehicleSubTab === tab.value;
  // `implicitPrimitive` is shared state across all primitive
  // sub-tabs (Implicit + NASA Lattice + Wheels). Only show its
  // label here when the Implicit Primitives tab is the active
  // one AND the selection is actually one of THIS tab's
  // primitives — NASA lattice slugs and the Lunar Rover Wheel
  // both live in the same union but belong to other tabs.
  const showsPicked =
    vehicleSubTab === 'implicit-primitives' &&
    PRIMITIVE_ORDER.includes(implicitPrimitive);
  const currentLabel = showsPicked
    ? IMPLICIT_PRIMITIVE_LABELS[implicitPrimitive]
    : 'Select Topology';
  // Swap params to the picked primitive's tutorial defaults so
  // the feature-tree sidebar is populated the instant the user
  // chooses a shape — no empty fields, no "fill these in first".
  const handlePick = (p: ImplicitPrimitive) => {
    setVehicleSubTab('implicit-primitives');
    setImplicitPrimitive(p);
    switch (p) {
      case 'base-box':
        setImplicitPrimitiveParams({
          kind: 'base-box',
          params: BASE_BOX_DEFAULTS,
        });
        break;
      case 'base-sphere':
        setImplicitPrimitiveParams({
          kind: 'base-sphere',
          params: BASE_SPHERE_DEFAULTS,
        });
        break;
      case 'base-cylinder':
        setImplicitPrimitiveParams({
          kind: 'base-cylinder',
          params: BASE_CYLINDER_DEFAULTS,
        });
        break;
      case 'base-pipe':
        setImplicitPrimitiveParams({
          kind: 'base-pipe',
          params: BASE_PIPE_DEFAULTS,
        });
        break;
      case 'base-pipe-segment':
        setImplicitPrimitiveParams({
          kind: 'base-pipe-segment',
          params: BASE_PIPE_SEGMENT_DEFAULTS,
        });
        break;
      case 'base-ring':
        setImplicitPrimitiveParams({
          kind: 'base-ring',
          params: BASE_RING_DEFAULTS,
        });
        break;
      case 'base-lens':
        setImplicitPrimitiveParams({
          kind: 'base-lens',
          params: BASE_LENS_DEFAULTS,
        });
        break;
      case 'super-ellipsoid':
        setImplicitPrimitiveParams({
          kind: 'super-ellipsoid',
          params: SUPER_ELLIPSOID_DEFAULTS,
        });
        break;
      case 'gyroid-sphere':
        setImplicitPrimitiveParams({
          kind: 'gyroid-sphere',
          params: GYROID_SPHERE_DEFAULTS,
        });
        break;
      case 'gyroid-genus':
        setImplicitPrimitiveParams({
          kind: 'gyroid-genus',
          params: GYROID_GENUS_DEFAULTS,
        });
        break;
      case 'lattice-pipe':
        setImplicitPrimitiveParams({
          kind: 'lattice-pipe',
          params: LATTICE_PIPE_DEFAULTS,
        });
        break;
      case 'lattice-manifold':
        setImplicitPrimitiveParams({
          kind: 'lattice-manifold',
          params: LATTICE_MANIFOLD_DEFAULTS,
        });
        break;
    }
  };
  return (
    <DropdownMenu key={tab.value}>
      {/* Stays interactive even after a model has been generated
          (matches the SUPIN dropdown), so users can iterate on
          primitives without starting a new conversation. */}
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            pillWidthClass,
            'flex flex-col items-center justify-center self-stretch overflow-hidden rounded-md border px-4 text-center text-sm transition-colors',
            isActive
              ? 'border-[#107d40] bg-[#070909] text-[#107d40] hover:border-[#107d40] hover:bg-[#0f3d24]'
              : 'border-[#737373] text-telekinesis-neutral-50 hover:border-[#107d40] hover:bg-[#0f3d24]',
          )}
        >
          {/* Shortened from tab.label ("Implicit Primitives") — doesn't fit
              two lines at the standardized pill width; the sidebar/dropdown
              still say "Implicit Primitives" in full. */}
          <span className="w-full truncate leading-tight">Primitives</span>
          <span className="mt-0.5 flex w-full items-center justify-center gap-1 text-xs leading-tight opacity-80">
            <span className="min-w-0 truncate">{currentLabel}</span>
            <ChevronDown className="h-3 w-3 flex-shrink-0" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={pickerContentClass}>
        {PRIMITIVE_ORDER.map((p) => (
          <DropdownMenuItem
            key={p}
            onClick={() => handlePick(p)}
            className={cn(
              'rounded-md border border-[#737373] bg-telekinesis-bg-dark px-4 py-2 hover:cursor-pointer hover:bg-[#0f3d24] focus:bg-[#0f3d24]',
              implicitPrimitive === p
                ? 'border-[#107d40] text-[#107d40]'
                : pickerItemInactiveClass,
            )}
          >
            <span className="text-sm font-medium">
              {IMPLICIT_PRIMITIVE_LABELS[p]}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
